import { memo } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { Helmet } from 'react-helmet'
import { platform } from 'utils'
import { store } from 'state/_store'
import { isAuthenticated } from 'state/Auth'
import { getOrdersCount } from 'state/Orders'
import { getHasReseller } from 'state/Reseller'

function Head() {
	const isAuth = useSelector(isAuthenticated)
	const isApp  = platform.isNative()
	// const appleId   = useSelector(getAccount)?.app_ids?.apple //the user might not be logged, and it's a new prop

	//the HTML[lang] is NOT set here. While the new Locale.useLang() works fine, it's too much trouble to change a single prop in a never-changing element directly available in JS. Check usages of setHtmlLang()
	return <Helmet>
		{/* No need of favicons when native - and don't try to wrap this into <> for a single check, Helmet will bitch */}
		{!isApp && <link rel="icon" sizes="16x16" href={mediaPath('favicon16', 'png', { folder: 'favicon' })}/>}
		{!isApp && <link rel="icon" sizes="32x32" href={mediaPath('favicon32', 'png', { folder: 'favicon' })}/>}

		{platform.isIOS() && <link rel="apple-touch-icon" href={mediaPath('media/logo192', 'png', { folder: 'favicon' })}/>}

		{/* Manifest and app links are only included after login, so we don't default to offering a generic app when the user is from eHungry but has yet to login */}
		{/* Note: you may need to hit F5 to update the loaded manifest if you switch between eHungry and Reseller */}
		{isAuth && <link rel="manifest" href={mediaPath('manifest', 'json', { force: true })}/>}
	</Helmet>

	/* Other stuff tried for Apple devices but didn't seem to be useful:
		<meta name="apple-mobile-web-app-capable" content="yes"/> same as manifest's display:standalone, but seems to do nothing?
		<meta name="format-detection" content="telephone=no"/> disables linkfying numbers formatted to look like a phone

		/* So far, the only method to suggest an app for iOS users - the manifest have no effect there. However, it also seems troublesome - see details at https://app.clubhouse.io/ehungry/story/20882/manager-admin-update-manifest-admin-with-new-app-urls#activity-27749
		{isAuth && appleId && <meta name="apple-itunes-app" content={`app-id=${appleId}`}/>}
	*/
}

function PageTitle({ title, forcedTitle }) {
	const fullTitle = makeTitle(title)
	return forcedTitle? <Helmet title={fullTitle} titleTemplate=""/> : <Helmet title={fullTitle}/>
}
PageTitle.propTypes = {
	title      : PropTypes.string.isRequired,
	forcedTitle: PropTypes.bool,
}

/* Those functions are not placed in utils as their use of state would cause circular imports... This could probably be avoided if they were placed in a separate file that's not imported by utils/index.js */

/**
 * Adds the proper counter to what you'll use as page title.
 * @param title {?string} "Meat" of the title, or null to reuse the current
 * @param notify {boolean} If a notification of "new orders" should be included
 * @return {string}
 */
function makeTitle(title = null, notify = false) {
	//https://regex101.com/r/R7taeC/1 - extracts the last portion, skipping counter and notif blocks
	title             = title || document.title.match(/^(?:\(\d+\) )?(?:\[.+\] )?(.+)$/)[1]
	const ordersCount = getOrdersCount(store.getState())['ACTIVE']
	return `${ordersCount > 0? `(${ordersCount})` : ''} ${notify? '[New!]' : ''} ${title}`
}

/**
 * Adds a suffix to the filename given the current reseller state - mainly to compose favicon/manifest paths.
 * @param filename
 * @param ext What should come after the dot
 * @param force Force a suffix (otherwise the reseller file stays without suffix)
 * @param folder
 * @return {*}
 * @FIXME drop "force" since it probably was just to avoid having any default manifest being "found"... since favicons are not in the default place either, they can also use -main and it clears one param here
 */
function mediaPath(filename, ext, { force = false, folder = null }) {
	const hasReseller = getHasReseller(store.getState())
	return [ //TODO: probably should have window.ROOT_URL too, but since it would only be needed in native apps...
		process.env.PUBLIC_URL + '/',
		folder? `${folder}/` : '',
		filename,
		hasReseller? (force? '-main' : '') : '-ehungry',
		'.' + ext,
	].join('')
}

const MemoedHead = memo(Head)
MemoedHead.makeTitle = makeTitle
MemoedHead.mediaPath = mediaPath
MemoedHead.PageTitle = memo(PageTitle)
export default MemoedHead
